import {
  ComponentProps,
  ForwardRefExoticComponent,
  RefAttributes,
  forwardRef,
} from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from '@backstage/core-components';
import type { RecentVisit } from './recentSearches';

// Link is typed as a plain function but is a forwardRef component at runtime.
// This cast lets us attach a ref to the underlying <a> element so the parent
// modal can call .click() for keyboard Enter navigation — same pattern as
// QuickSearchResultItem.
const ForwardableLink = Link as ForwardRefExoticComponent<
  ComponentProps<typeof Link> & RefAttributes<HTMLAnchorElement>
>;

export type RecentSearchItemProps = {
  visit: RecentVisit;
  rank: number; // 1-based; used for aria-posinset
  selected: boolean; // true when this row is keyboard-highlighted
  onActivate: () => void; // called on mouse click; Enter is handled at modal level
  onRemove: () => void; // called when the × button is clicked
};

/**
 * A single row in the "Recent" panel shown when the search input is empty.
 *
 * Mirrors the selection styling of QuickSearchResultItem exactly so keyboard
 * navigation feels identical. Unlike QuickSearchResultItem:
 *   - No search-hit highlighting (there is no active query).
 *   - Smaller chip row — only sourceLabel and kind when present.
 *   - Trailing × button that removes the entry from the MRU list.
 *
 * DOM structure: the × IconButton is a SIBLING of the ForwardableLink, not
 * nested inside it. Nesting a <button> inside an <a> is invalid HTML and
 * confuses screen readers. The outer Box uses display:flex so the link fills
 * remaining space and the button sits flush to the right.
 */
export const RecentSearchItem = forwardRef<
  HTMLAnchorElement,
  RecentSearchItemProps
>(function RecentSearchItem(
  { visit, rank, selected, onActivate, onRemove },
  ref,
) {
  return (
    // Box owns all visual state (selection highlight, hover, ARIA).
    // The exact bgcolor/outline/hover values mirror QuickSearchResultItem so
    // toggling between live results and recents feels seamless.
    <Box
      role="option"
      aria-selected={selected}
      aria-posinset={rank}
      sx={{
        display: 'flex',
        alignItems: 'center',
        bgcolor: selected
          ? theme => alpha(theme.palette.primary.main, 0.1)
          : 'transparent',
        outline: selected
          ? theme => `2px solid ${alpha(theme.palette.primary.main, 0.3)}`
          : 'none',
        outlineOffset: '-2px',
        borderRadius: 1,
        '&:hover': {
          bgcolor: theme => alpha(theme.palette.primary.main, 0.06),
        },
      }}
    >
      {/* The link fills all available horizontal space. tabIndex={-1} keeps
          focus on the search input while still allowing .click() for Enter
          key activation from the modal keyboard handler. */}
      <ForwardableLink
        ref={ref}
        to={visit.location}
        onClick={onActivate}
        tabIndex={-1}
        noTrack={false}
        style={{
          flex: 1,
          padding: '8px 16px',
          textDecoration: 'none',
        }}
      >
        <Stack direction="column" spacing={0.25}>
          <Typography noWrap variant="body2">
            {visit.title}
          </Typography>

          {/* Only render the chip row when there is something to show.
              sourceLabel is optional on RecentVisit (though typically present),
              and kind is always optional. */}
          {(visit.sourceLabel || visit.kind) && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {visit.sourceLabel && (
                <Chip size="small" label={visit.sourceLabel} />
              )}
              {visit.kind && <Chip size="small" label={visit.kind} />}
            </Stack>
          )}
        </Stack>
      </ForwardableLink>

      {/* × button — sibling of the link, never nested inside it.
          stopPropagation prevents the Box's click from firing;
          preventDefault prevents any synthetic anchor activation. */}
      <IconButton
        size="small"
        aria-label={`Remove ${visit.title} from recent searches`}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
        sx={{ mr: 0.5, flexShrink: 0 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
});
